import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getSharedTasks,
  acceptSharedTask,
  declineSharedTask,
} from "@/data/requests/shared-task-requests";
import {
  getSharedHabits,
  acceptSharedHabit,
  declineSharedHabit,
} from "@/data/requests/shared-habit-requests";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Check, X, User, Clock, Flame } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";

export function SharedItemsList() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const { data: sharedTasksResponse, isLoading: isLoadingTasks } = useQuery({
    queryKey: ["shared-tasks"],
    queryFn: () => getSharedTasks(),
  });

  const { data: sharedHabitsResponse, isLoading: isLoadingHabits } = useQuery({
    queryKey: ["shared-habits"],
    queryFn: () => getSharedHabits(),
  });

  const sharedTasks = sharedTasksResponse?.data?.data || [];
  const sharedHabits = sharedHabitsResponse?.data?.data || [];

  const handleTaskAction = useMutation({
    mutationFn: ({ id, action }) => action === "accept" ? acceptSharedTask(id) : declineSharedTask(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shared-tasks"] });
      toast({ title: t("sharedInbox.toast.taskSuccess") });
    },
    onError: () =>
      toast({
        title: t("sharedInbox.toast.taskError"),
        variant: "destructive",
      }),
  });

  const handleHabitAction = useMutation({
    mutationFn: ({ id, action }) => action === "accept" ? acceptSharedHabit(id) : declineSharedHabit(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shared-habits"] });
      toast({ title: t("sharedInbox.toast.habitSuccess") });
    },
    onError: () =>
      toast({
        title: t("sharedInbox.toast.habitError"),
        variant: "destructive",
      }),
  });

  const renderSharedItem = (item, type) => {
    const isTask = type === "task";
    const mutation = isTask ? handleTaskAction : handleHabitAction;
    const content = isTask ? item.task : item.habit;
    const sender = item.sharedBy;

    return (
      <Card key={item.id} className="mb-4">
        <CardHeader className="pb-2">
          <div className="flex justify-between items-start">
            <div className="flex items-center gap-2">
              <Avatar className="h-8 w-8">
                <AvatarImage src={sender.avatar} />
                <AvatarFallback>{sender.name?.charAt(0).toUpperCase() || <User className="h-4 w-4" />}</AvatarFallback>
              </Avatar>
              <div>
                <CardTitle className="text-base">
                  {content.title || content.name}
                </CardTitle>
                <CardDescription className="text-xs">
                  {t("sharedInbox.item.sharedBy")}{" "}
                  <span className="font-medium text-foreground">
                    {sender.name}
                  </span>
                </CardDescription>
              </div>
            </div>
            <Badge
              variant={item.status === "PENDING" ? "secondary" : "outline"}
            >
              {t(`sharedInbox.status.${item.status.toLowerCase()}`, {
                defaultValue: item.status,
              })}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="pb-2">
          <p className="text-sm text-muted-foreground mb-2">
            {content.description || t("sharedInbox.item.noDescription")}
          </p>
          <div className="flex gap-2 text-xs text-muted-foreground">
            {isTask && content.dueDate && (
              <div className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {format(new Date(content.dueDate), "dd.MM HH:mm")}
              </div>
            )}
            {!isTask && content.cadence && (
              <div className="flex items-center gap-1">
                <Flame className="h-3 w-3" />
                {content.cadence}
              </div>
            )}
          </div>
        </CardContent>
        {item.status === "PENDING" && (
          <CardFooter className="pt-2 flex gap-2 justify-end">
            <Button
              size="sm"
              variant="outline"
              className="text-red-500 hover:text-red-600"
              onClick={() =>
                mutation.mutate({ id: item.id, action: "decline" })
              }
              disabled={mutation.isPending}
            >
              <X className="h-4 w-4 mr-1" />{" "}
              {t("sharedInbox.actions.decline")}
            </Button>
            <Button
              size="sm"
              onClick={() =>
                mutation.mutate({ id: item.id, action: "accept" })
              }
              disabled={mutation.isPending}
            >
              <Check className="h-4 w-4 mr-1" />{" "}
              {t("sharedInbox.actions.accept")}
            </Button>
          </CardFooter>
        )}
      </Card>
    );
  };

  return (
    <Tabs defaultValue="tasks" className="w-full">
      <TabsList className="grid w-full grid-cols-2 mb-4">
        <TabsTrigger value="tasks">
          {t("sharedInbox.tabs.tasks", {
            count: sharedTasks.filter((t) => t.status === "PENDING").length,
          })}
        </TabsTrigger>
        <TabsTrigger value="habits">
          {t("sharedInbox.tabs.habits", {
            count: sharedHabits.filter((h) => h.status === "PENDING").length,
          })}
        </TabsTrigger>
      </TabsList>
      
      <TabsContent value="tasks">
        {isLoadingTasks ? (
          <div className="text-center py-8">
            {t("sharedInbox.loadingTasks")}
          </div>
        ) : sharedTasks.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            {t("sharedInbox.emptyTasks")}
          </div>
        ) : (
          sharedTasks.map((item) => renderSharedItem(item, "task"))
        )}
      </TabsContent>
      
      <TabsContent value="habits">
        {isLoadingHabits ? (
          <div className="text-center py-8">
            {t("sharedInbox.loadingHabits")}
          </div>
        ) : sharedHabits.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            {t("sharedInbox.emptyHabits")}
          </div>
        ) : (
          sharedHabits.map((item) => renderSharedItem(item, "habit"))
        )}
      </TabsContent>
    </Tabs>
  );
}
